import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Alert, Modal, Button, TextInput, Textarea, Text, Stack, Group, SimpleGrid } from "@mantine/core";
import { useAuthStore } from "@/lib/auth";
import { meetingRoomsApi } from "@/lib/api/client";
import { BookingCostSummary, estimateCharge, formatKr, type RoomPricing } from "@nodus/booking";
import { useApprovalSettings } from "@/hooks/use-booking-approvals";
import { approvalNotice, bookingResultMessage } from "@/lib/booking-approvals";
import { toast } from "sonner";

interface BookingModalProps {
  opened: boolean;
  onClose: () => void;
  roomId: number;
  roomName: string;
  initialStart: string;  // ISO
  initialEnd: string;    // ISO
  onSuccess?: () => void;
  pricing?: RoomPricing | null;
}

function toLocalDateTimeValue(iso: string) {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function toIso(local: string): string | null {
  const d = new Date(local);
  return local && !Number.isNaN(d.getTime()) ? d.toISOString() : null;
}

export function BookingModal({ opened, onClose, roomId, roomName, initialStart, initialEnd, onSuccess, pricing }: BookingModalProps) {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();

  const [title, setTitle] = useState(`Meeting - ${roomName}`);
  const [start, setStart] = useState(toLocalDateTimeValue(initialStart));
  const [end, setEnd] = useState(toLocalDateTimeValue(initialEnd));
  const [description, setDescription] = useState("");

  useEffect(() => {
    if (opened) {
      setTitle(`Meeting - ${roomName}`);
      setStart(toLocalDateTimeValue(initialStart));
      setEnd(toLocalDateTimeValue(initialEnd));
      setDescription("");
    }
  }, [opened, roomName, initialStart, initialEnd]);

  const startIso = toIso(start);
  const endIso = toIso(end);
  const estimate = startIso && endIso ? estimateCharge(pricing, startIso, endIso) : null;

  const { data: approvalSettings } = useApprovalSettings();
  const notice = startIso
    ? approvalNotice({ settings: approvalSettings, estimate: estimate?.amount ?? null, start: startIso })
    : null;

  const bookedByLine = user
    ? `Booked by: ${user.email}${user.tenant_name ? ` (${user.tenant_name})` : ""}`
    : "";

  const mutation = useMutation({
    mutationFn: () =>
      meetingRoomsApi.bookRoom(roomId, {
        title,
        start: new Date(start).toISOString(),
        end: new Date(end).toISOString(),
        description: [description.trim(), bookedByLine].filter(Boolean).join("\n\n"),
      }),
    onSuccess: (res) => {
      toast.success(bookingResultMessage(res.data.approval, res.data.start, res.data.site_time_zone));
      queryClient.invalidateQueries({ queryKey: ["meeting-rooms"] });
      queryClient.invalidateQueries({ queryKey: ["meeting-room-detail", String(roomId)] });
      onSuccess?.();
      onClose();
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.detail || "Failed to book room. Please try again.";
      toast.error(msg);
    },
  });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`Book ${roomName}`}
      size="sm"
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">Fill in the details to reserve this room.</Text>

        <TextInput
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.currentTarget.value)}
          disabled={mutation.isPending}
        />

        <SimpleGrid cols={2}>
          <TextInput
            label="Start"
            type="datetime-local"
            value={start}
            onChange={(e) => setStart(e.currentTarget.value)}
            disabled={mutation.isPending}
          />
          <TextInput
            label="End"
            type="datetime-local"
            value={end}
            onChange={(e) => setEnd(e.currentTarget.value)}
            disabled={mutation.isPending}
          />
        </SimpleGrid>

        {startIso && endIso && <BookingCostSummary pricing={pricing} start={startIso} end={endIso} />}

        {notice && (
          <Alert color="orange" variant="light" data-testid="approval-notice">
            {notice}
          </Alert>
        )}

        <Textarea
          label="Description"
          placeholder="Optional notes..."
          value={description}
          onChange={(e) => setDescription(e.currentTarget.value)}
          rows={3}
          disabled={mutation.isPending}
        />

        {bookedByLine && (
          <Text size="xs" c="dimmed" pt="sm" style={{ borderTop: "1px solid var(--mantine-color-default-border)" }}>
            {bookedByLine}
          </Text>
        )}

        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button onClick={() => mutation.mutate()} disabled={mutation.isPending || !title.trim()}>
            {mutation.isPending
              ? "Booking\u2026"
              : notice
                ? "Send request"
                : estimate
                  ? `Book for ${estimate.upTo ? "up to " : ""}${formatKr(estimate.amount)} kr`
                  : "Book Room"}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
